import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// ═══════════════════════════════════════════════════════════════════════════════
// STONEART MISSION CONTROL v7
// ═══════════════════════════════════════════════════════════════════════════════

const VERSION = 'v7';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://sqggfamidvkrmypwqdxo.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNxZ2dmYW1pZHZrcm15cHdxZHhvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzAxMTg2ODUsImV4cCI6MjA4NTY5NDY4NX0.69-DfEXznyreiGagONUJFTDBjLrF8Wev4sSKspOYXVc';
const supabase = createClient(supabaseUrl, supabaseKey);

// ═══════════════════════════════════════════════════════════════════════════════
// TEAM CREDENTIALS - Case insensitive email matching
// ═══════════════════════════════════════════════════════════════════════════════
const TEAM_CREDENTIALS = [
  { email: 'connect@stoneart.media', password: 'LBSXCz9qmSw5VndW', name: 'Maxo', color: '#1565C0' },
  { email: 'marion_mielke@web.de', password: 'jMHmbZA3nClL1CnT', name: 'Mary', color: '#E31E24' },
  { email: 'suntic.2012@gmail.com', password: '0hDy6EUDjVn6LbJI', name: 'Sunti', color: '#2E7D32' }
];

function App() {
  // ═══════════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION STATE - This controls everything
  // ═══════════════════════════════════════════════════════════════════════════════
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════════
  // APP STATE
  // ═══════════════════════════════════════════════════════════════════════════════
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dataLoading, setDataLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [actions, setActions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [brandIdeas, setBrandIdeas] = useState([]);
  
  // UI States
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [showNewActionForm, setShowNewActionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [stepNotes, setStepNotes] = useState('');
  const [newIdeaText, setNewIdeaText] = useState('');
  const [editingIdea, setEditingIdea] = useState(null);
  const [ideaNotes, setIdeaNotes] = useState('');
  const [newActionTitle, setNewActionTitle] = useState('');

  // ═══════════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION LOGIC
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = () => {
      const savedSession = localStorage.getItem('stoneart_session');
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          // Verify session is still valid (check if user exists in credentials)
          const validUser = TEAM_CREDENTIALS.find(
            cred => cred.email.toLowerCase() === session.email.toLowerCase()
          );
          if (validUser) {
            setCurrentUser({
              email: validUser.email,
              name: validUser.name,
              color: validUser.color
            });
            setIsAuthenticated(true);
          } else {
            // Invalid session, clear it
            localStorage.removeItem('stoneart_session');
          }
        } catch (e) {
          localStorage.removeItem('stoneart_session');
        }
      }
      setAuthChecked(true);
    };
    
    checkExistingSession();
  }, []);

  // Load data only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu && !e.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // LOGIN / LOGOUT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleLogin = (e) => {
    if (e) e.preventDefault();
    setLoginError('');
    
    const emailInput = loginEmail.toLowerCase().trim();
    const passwordInput = loginPassword;
    
    // Validate inputs
    if (!emailInput) {
      setLoginError('Bitte E-Mail eingeben');
      return;
    }
    if (!passwordInput) {
      setLoginError('Bitte Passwort eingeben');
      return;
    }
    
    // Find matching credentials
    const matchingUser = TEAM_CREDENTIALS.find(
      cred => cred.email.toLowerCase() === emailInput
    );
    
    if (!matchingUser) {
      setLoginError('E-Mail nicht gefunden');
      return;
    }
    
    if (passwordInput !== matchingUser.password) {
      setLoginError('Falsches Passwort');
      return;
    }
    
    // Success! Set user and save session
    const userSession = {
      email: matchingUser.email,
      name: matchingUser.name,
      color: matchingUser.color,
      loginTime: new Date().toISOString()
    };
    
    localStorage.setItem('stoneart_session', JSON.stringify(userSession));
    setCurrentUser(userSession);
    setIsAuthenticated(true);
    setLoginEmail('');
    setLoginPassword('');
    setLoginError('');
  };

  const handleLogout = () => {
    localStorage.removeItem('stoneart_session');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setShowUserMenu(false);
    setActiveTab('dashboard');
    setSelectedCustomer(null);
    setSelectedProject(null);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadAllData = async () => {
    setDataLoading(true);
    try {
      const [teamRes, customersRes, projectsRes, actionsRes, goalsRes, activityRes, ideasRes] = await Promise.all([
        supabase.from('team_members').select('*'),
        supabase.from('customers').select('*').order('created_at', { ascending: false }),
        supabase.from('projects').select('*').order('created_at', { ascending: false }),
        supabase.from('actions').select('*').order('created_at', { ascending: false }),
        supabase.from('goals').select('*'),
        supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(50),
        supabase.from('brand_ideas').select('*').order('created_at', { ascending: false })
      ]);
      
      setTeamMembers(teamRes.data || []);
      setCustomers(customersRes.data || []);
      setProjects(projectsRes.data || []);
      setActions(actionsRes.data || []);
      setGoals(goalsRes.data || []);
      setActivityLog(activityRes.data || []);
      setBrandIdeas(ideasRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setDataLoading(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  const getTeamMemberById = (id) => teamMembers.find(t => t.id === id);
  const getTeamMemberColor = (id) => getTeamMemberById(id)?.color || '#666';
  const getTeamMemberName = (id) => getTeamMemberById(id)?.name || 'Unbekannt';

  const generateProjectSteps = (startDate) => {
    const start = new Date(startDate);
    const addDays = (date, days) => {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result.toISOString().split('T')[0];
    };
    
    return [
      { id: 1, name: 'Strategiegespräch', status: 'pending', deadline: start.toISOString().split('T')[0], notes: '' },
      { id: 2, name: 'Kick-Off Call', status: 'pending', deadline: addDays(start, 3), notes: '' },
      { id: 3, name: 'Strategie System', status: 'pending', deadline: addDays(start, 5), notes: '' },
      { id: 4, name: '1. Drehtag', status: 'pending', deadline: addDays(start, 10), notes: '' },
      { id: 5, name: 'Schnitt/Postproduktion', status: 'pending', deadline: addDays(start, 17), notes: '' },
      { id: 6, name: 'Content geplant', status: 'pending', deadline: addDays(start, 21), notes: '' },
      { id: 7, name: '1. Report', status: 'pending', deadline: addDays(start, 30), notes: '' },
      { id: 8, name: '3-Monats-Review', status: 'pending', deadline: addDays(start, 90), notes: '' }
    ];
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  const logActivity = async (actionType, description, entityType = null, entityId = null) => {
    const teamMember = teamMembers.find(t => t.email.toLowerCase() === currentUser?.email?.toLowerCase());
    if (!teamMember) return;
    
    await supabase.from('activity_log').insert({
      team_member_id: teamMember.id,
      action_type: actionType,
      description,
      entity_type: entityType,
      entity_id: entityId
    });
  };

  const createCustomer = async (customerData) => {
    const teamMember = teamMembers.find(t => t.email.toLowerCase() === currentUser?.email?.toLowerCase());
    
    try {
      const { data, error } = await supabase.from('customers').insert({
        ...customerData,
        created_by: teamMember?.id || null
      }).select().single();
      
      if (error) {
        console.error('Error creating customer:', error);
        alert('Fehler beim Erstellen des Kunden: ' + error.message);
        return { data: null, error };
      }
      
      if (data) {
        setCustomers(prev => [data, ...prev]);
        await logActivity('create', `Neuer Kunde "${data.firma}" angelegt`, 'customer', data.id);
        
        // Create project
        const projectData = {
          customer_id: data.id,
          customer_name: data.firma,
          paket: data.paket || null,
          steps: generateProjectSteps(new Date().toISOString().split('T')[0])
        };
        
        const { data: project, error: projectError } = await supabase.from('projects').insert(projectData).select().single();
        
        if (projectError) {
          console.error('Error creating project:', projectError);
        } else if (project) {
          setProjects(prev => [project, ...prev]);
        }
      }
      return { data, error: null };
    } catch (err) {
      console.error('Exception in createCustomer:', err);
      return { data: null, error: err };
    }
  };

  const updateCustomer = async (id, updates) => {
    const { error } = await supabase.from('customers').update(updates).eq('id', id);
    if (!error) {
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
      if (selectedCustomer?.id === id) {
        setSelectedCustomer(prev => ({ ...prev, ...updates }));
      }
      await logActivity('update', `Kunde aktualisiert`, 'customer', id);
    }
  };

  const deleteCustomer = async (id) => {
    const customer = customers.find(c => c.id === id);
    await supabase.from('customers').delete().eq('id', id);
    setCustomers(prev => prev.filter(c => c.id !== id));
    setSelectedCustomer(null);
    setShowDeleteConfirm(null);
    await logActivity('delete', `Kunde "${customer?.firma}" gelöscht`, 'customer', id);
  };

  const createAction = async (title) => {
    const teamMember = teamMembers.find(t => t.email.toLowerCase() === currentUser?.email?.toLowerCase());
    
    const { data } = await supabase.from('actions').insert({
      title,
      status: 'pending',
      priority: 'medium',
      created_by: teamMember?.id || null
    }).select().single();
    
    if (data) {
      setActions(prev => [data, ...prev]);
      await logActivity('create', `Action "${title}" erstellt`, 'action', data.id);
    }
  };

  const toggleAction = async (id) => {
    const action = actions.find(a => a.id === id);
    const newStatus = action.status === 'completed' ? 'pending' : 'completed';
    await supabase.from('actions').update({ status: newStatus }).eq('id', id);
    setActions(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
  };

  const toggleStepStatus = async (projectId, stepId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const teamMember = teamMembers.find(t => t.email.toLowerCase() === currentUser?.email?.toLowerCase());
    
    const updatedSteps = project.steps.map(step => {
      if (step.id === stepId) {
        const newStatus = step.status === 'completed' ? 'pending' : 'completed';
        return { 
          ...step, 
          status: newStatus,
          completed_by: newStatus === 'completed' ? teamMember?.id : null,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null
        };
      }
      return step;
    });
    
    await supabase.from('projects').update({ steps: updatedSteps }).eq('id', projectId);
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, steps: updatedSteps } : p));
    if (selectedProject?.id === projectId) {
      setSelectedProject(prev => ({ ...prev, steps: updatedSteps }));
    }
  };

  const updateStepNotes = async (projectId, stepId, notes) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const updatedSteps = project.steps.map(step => 
      step.id === stepId ? { ...step, notes } : step
    );
    
    await supabase.from('projects').update({ steps: updatedSteps }).eq('id', projectId);
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, steps: updatedSteps } : p));
    if (selectedProject?.id === projectId) {
      setSelectedProject(prev => ({ ...prev, steps: updatedSteps }));
    }
  };

  const createIdea = async (text) => {
    const teamMember = teamMembers.find(t => t.email.toLowerCase() === currentUser?.email?.toLowerCase());
    
    const { data } = await supabase.from('brand_ideas').insert({
      text,
      notes: '',
      created_by: teamMember?.id || null
    }).select().single();
    
    if (data) {
      setBrandIdeas(prev => [data, ...prev]);
      await logActivity('create', `Neue Idee hinzugefügt`, 'idea', data.id);
    }
  };

  const updateIdeaNotes = async (id, notes) => {
    await supabase.from('brand_ideas').update({ notes }).eq('id', id);
    setBrandIdeas(prev => prev.map(i => i.id === id ? { ...i, notes } : i));
  };

  const deleteIdea = async (id) => {
    await supabase.from('brand_ideas').delete().eq('id', id);
    setBrandIdeas(prev => prev.filter(i => i.id !== id));
    setEditingIdea(null);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // STYLES
  // ═══════════════════════════════════════════════════════════════════════════════
  const styles = {
    container: { minHeight: '100vh', background: '#FFFFFF', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", color: '#1A1A1A' },
    header: { background: '#FFFFFF', borderBottom: '3px solid #E31E24', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logo: { fontSize: '1.5rem', fontWeight: '800', fontStyle: 'italic' },
    logoRed: { background: '#E31E24', color: '#FFF', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '10px' },
    versionBadge: { background: 'rgba(255,255,255,0.2)', padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600' },
    nav: { display: 'flex', gap: '5px', padding: '10px 20px', background: '#FAFAFA', borderBottom: '1px solid #E5E5E5', flexWrap: 'wrap' },
    navBtn: { padding: '12px 20px', background: 'transparent', border: '2px solid #E31E24', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: '#1A1A1A', transition: 'all 0.2s' },
    navBtnActive: { background: '#E31E24', color: '#FFFFFF' },
    content: { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
    card: { background: '#FFFFFF', border: '1px solid #E5E5E5', padding: '20px', marginBottom: '15px' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '10px', borderBottom: '2px solid #E31E24' },
    cardTitle: { fontSize: '1rem', fontWeight: '700', margin: 0 },
    primaryBtn: { padding: '10px 20px', background: '#FFFFFF', border: '2px solid #E31E24', color: '#1A1A1A', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' },
    primaryBtnFilled: { padding: '10px 20px', background: '#E31E24', border: '2px solid #E31E24', color: '#FFFFFF', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' },
    dangerBtn: { padding: '10px 20px', background: '#FFFFFF', border: '2px solid #D32F2F', color: '#D32F2F', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' },
    input: { width: '100%', padding: '12px', border: '2px solid #E5E5E5', fontSize: '0.9rem', marginBottom: '10px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '12px', border: '2px solid #E5E5E5', fontSize: '0.9rem', marginBottom: '10px', background: '#FFF' },
    textarea: { width: '100%', padding: '12px', border: '2px solid #E5E5E5', fontSize: '0.9rem', marginBottom: '10px', minHeight: '100px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' },
    badge: { display: 'inline-block', padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600' },
    badgeActive: { background: '#E8F5E9', color: '#2E7D32' },
    checkbox: { width: '22px', height: '22px', border: '2px solid #E31E24', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 },
    checkboxChecked: { background: '#E31E24', color: '#FFFFFF' },
    stepItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderBottom: '1px solid #F0F0F0' },
    stat: { textAlign: 'center', padding: '25px 20px', border: '2px solid #E31E24' },
    statNumber: { fontSize: '2.5rem', fontWeight: '800', color: '#E31E24' },
    statLabel: { fontSize: '0.8rem', color: '#666', marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    userBadge: { display: 'flex', alignItems: 'center', gap: '10px' },
    userDot: { width: '12px', height: '12px', borderRadius: '50%' },
    loginBox: { maxWidth: '400px', margin: '100px auto', padding: '40px', border: '2px solid #E31E24', background: '#FFF' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#FFF', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', border: '1px solid #E5E5E5', padding: '20px' }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: AUTH CHECK IN PROGRESS
  // ═══════════════════════════════════════════════════════════════════════════════
  if (!authChecked) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.logoRed}>
            <span style={styles.logo}>Stone<span style={{ fontWeight: '300' }}>ART.</span></span>
          </div>
          <p style={{ marginTop: '20px', color: '#666' }}>Lädt...</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: LOGIN SCREEN (when not authenticated)
  // ═══════════════════════════════════════════════════════════════════════════════
  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.logoRed}>
            <span style={styles.logo}>Stone<span style={{ fontWeight: '300' }}>ART.</span></span>
            <span style={styles.versionBadge}>{VERSION}</span>
          </div>
        </header>
        
        <div style={styles.loginBox}>
          <h2 style={{ marginBottom: '25px', textAlign: 'center' }}>Mission Control Login</h2>
          
          {loginError && (
            <div style={{ background: '#FFEBEE', color: '#C62828', padding: '12px', marginBottom: '15px', textAlign: 'center', fontWeight: '600' }}>
              ⚠️ {loginError}
            </div>
          )}
          
          <form onSubmit={handleLogin}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>E-Mail</label>
            <input
              style={styles.input}
              type="email"
              placeholder="deine@email.de"
              value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Passwort</label>
            <input
              style={styles.input}
              type="password"
              placeholder="••••••••"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              autoComplete="current-password"
            />
            
            <button 
              type="submit"
              style={{ ...styles.primaryBtnFilled, width: '100%', marginTop: '15px', padding: '15px' }}
            >
              Einloggen
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // DASHBOARD VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  const DashboardView = () => {
    const activeCustomers = customers.filter(c => c.status === 'aktiv').length;
    const openActions = actions.filter(a => a.status === 'pending').length;
    
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px' }}>Dashboard</h2>
        
        <div style={styles.grid}>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{activeCustomers}</div>
            <div style={styles.statLabel}>Aktive Kunden</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{openActions}</div>
            <div style={styles.statLabel}>Offene Actions</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{projects.length}</div>
            <div style={styles.statLabel}>Projekte</div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ ...styles.card, marginTop: '20px' }}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>⚡ Actions</h3>
            <button style={styles.primaryBtn} onClick={() => setShowNewActionForm(!showNewActionForm)}>
              + Neue Action
            </button>
          </div>
          
          {showNewActionForm && (
            <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
              <input 
                style={{ ...styles.input, marginBottom: 0, flex: 1 }} 
                placeholder="Neue Action..."
                value={newActionTitle}
                onChange={e => setNewActionTitle(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter' && newActionTitle.trim()) {
                    createAction(newActionTitle);
                    setNewActionTitle('');
                    setShowNewActionForm(false);
                  }
                }}
              />
              <button style={styles.primaryBtnFilled} onClick={() => {
                if (newActionTitle.trim()) {
                  createAction(newActionTitle);
                  setNewActionTitle('');
                  setShowNewActionForm(false);
                }
              }}>Hinzufügen</button>
            </div>
          )}

          {actions.filter(a => a.status === 'pending').length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>Keine offenen Actions 🎉</p>
          ) : (
            actions.filter(a => a.status === 'pending').map(action => (
              <div key={action.id} style={{
                ...styles.stepItem,
                borderLeftColor: getTeamMemberColor(action.created_by),
                borderLeftWidth: '3px',
                borderLeftStyle: 'solid'
              }}>
                <div style={{ ...styles.checkbox }} onClick={() => toggleAction(action.id)}>
                  {action.status === 'completed' && '✓'}
                </div>
                <div style={{ flex: 1 }}>
                  <span>{action.title}</span>
                  {action.created_by && (
                    <span style={{ fontSize: '0.75rem', color: getTeamMemberColor(action.created_by), marginLeft: '10px' }}>
                      von {getTeamMemberName(action.created_by)}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Activity Log */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📋 Letzte Aktivitäten</h3>
          </div>
          {activityLog.slice(0, 10).map(activity => (
            <div key={activity.id} style={{ 
              padding: '10px 12px', 
              borderLeft: `3px solid ${getTeamMemberColor(activity.team_member_id)}`,
              marginBottom: '8px',
              background: '#FAFAFA',
              fontSize: '0.85rem'
            }}>
              <strong style={{ color: getTeamMemberColor(activity.team_member_id) }}>
                {getTeamMemberName(activity.team_member_id)}
              </strong>
              {' '}{activity.description}
              <span style={{ color: '#999', marginLeft: '10px', fontSize: '0.75rem' }}>
                {new Date(activity.created_at).toLocaleDateString('de-DE')}
              </span>
            </div>
          ))}
          {activityLog.length === 0 && (
            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>Noch keine Aktivitäten</p>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // KUNDEN VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  const KundenView = () => {
    const [newCustomer, setNewCustomer] = useState({
      firma: '', ansprechpartner: '', email: '', telefon: '',
      ziele: [], paket: '', notizen: ''
    });

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>Kunden</h2>
          <button style={styles.primaryBtnFilled} onClick={() => setShowNewCustomerForm(true)}>
            + Neuer Kunde
          </button>
        </div>

        {/* New Customer Form */}
        {showNewCustomerForm && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Neuen Kunden anlegen</h3>
              <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setShowNewCustomerForm(false)}>✕</button>
            </div>
            <div style={styles.grid}>
              <div>
                <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Firma *</label>
                <input style={styles.input} placeholder="Firmenname"
                  value={newCustomer.firma} onChange={e => setNewCustomer({ ...newCustomer, firma: e.target.value })} />
              </div>
              <div>
                <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Ansprechpartner *</label>
                <input style={styles.input} placeholder="Name"
                  value={newCustomer.ansprechpartner} onChange={e => setNewCustomer({ ...newCustomer, ansprechpartner: e.target.value })} />
              </div>
              <div>
                <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>E-Mail</label>
                <input style={styles.input} placeholder="email@beispiel.de" type="email"
                  value={newCustomer.email} onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })} />
              </div>
              <div>
                <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Telefon</label>
                <input style={styles.input} placeholder="+49..."
                  value={newCustomer.telefon} onChange={e => setNewCustomer({ ...newCustomer, telefon: e.target.value })} />
              </div>
            </div>
            
            <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Paket</label>
            <select style={styles.select} value={newCustomer.paket} onChange={e => setNewCustomer({ ...newCustomer, paket: e.target.value })}>
              <option value="">Später auswählen</option>
              <option value="visibility">VISIBILITY - 3.500€/Monat</option>
              <option value="growth">GROWTH - 5.500€/Monat</option>
              <option value="scale">SCALE - ab 9.000€/Monat</option>
            </select>

            <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Notizen</label>
            <textarea style={styles.textarea} placeholder="Interne Notizen..."
              value={newCustomer.notizen} onChange={e => setNewCustomer({ ...newCustomer, notizen: e.target.value })} />

            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={styles.primaryBtnFilled} onClick={async () => {
                if (newCustomer.firma && newCustomer.ansprechpartner) {
                  await createCustomer(newCustomer);
                  setNewCustomer({ firma: '', ansprechpartner: '', email: '', telefon: '', ziele: [], paket: '', notizen: '' });
                  setShowNewCustomerForm(false);
                }
              }}>Kunde anlegen</button>
              <button style={styles.primaryBtn} onClick={() => setShowNewCustomerForm(false)}>Abbrechen</button>
            </div>
          </div>
        )}

        {/* Customer Detail or List */}
        {selectedCustomer ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>{selectedCustomer.firma}</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setEditingCustomer({...selectedCustomer})}>✏️ Bearbeiten</button>
                <button style={{ ...styles.dangerBtn, padding: '8px 16px' }} onClick={() => setShowDeleteConfirm(selectedCustomer)}>🗑️ Löschen</button>
                <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setSelectedCustomer(null)}>← Zurück</button>
              </div>
            </div>
            
            <div style={styles.grid}>
              <div>
                <p><strong>Ansprechpartner:</strong> {selectedCustomer.ansprechpartner}</p>
                <p><strong>E-Mail:</strong> {selectedCustomer.email || '-'}</p>
                <p><strong>Telefon:</strong> {selectedCustomer.telefon || '-'}</p>
              </div>
              <div>
                <p><strong>Paket:</strong> {selectedCustomer.paket ? 
                  <span style={{ ...styles.badge, background: '#E3F2FD', color: '#1565C0' }}>{selectedCustomer.paket.toUpperCase()}</span> : 
                  <span style={{ color: '#999' }}>Noch nicht ausgewählt</span>}
                </p>
                <p><strong>Status:</strong> <span style={{ ...styles.badge, ...styles.badgeActive }}>{selectedCustomer.status}</span></p>
              </div>
            </div>

            {selectedCustomer.notizen && (
              <div style={{ marginTop: '15px', padding: '15px', background: '#FAFAFA', border: '1px solid #E5E5E5' }}>
                <strong>Notizen:</strong>
                <p style={{ margin: '10px 0 0 0', whiteSpace: 'pre-wrap' }}>{selectedCustomer.notizen}</p>
              </div>
            )}

            {selectedCustomer.created_by && (
              <p style={{ marginTop: '15px', fontSize: '0.85rem', color: '#666' }}>
                Erstellt von <span style={{ color: getTeamMemberColor(selectedCustomer.created_by), fontWeight: '600' }}>
                  {getTeamMemberName(selectedCustomer.created_by)}
                </span> am {new Date(selectedCustomer.created_at).toLocaleDateString('de-DE')}
              </p>
            )}
          </div>
        ) : (
          <div style={styles.card}>
            {customers.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Noch keine Kunden angelegt</p>
            ) : (
              customers.map(customer => (
                <div 
                  key={customer.id} 
                  style={{ 
                    padding: '15px', 
                    borderBottom: '1px solid #E5E5E5', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderLeft: `3px solid ${getTeamMemberColor(customer.created_by)}`
                  }}
                  onClick={() => setSelectedCustomer(customer)}
                >
                  <div>
                    <strong>{customer.firma}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>{customer.ansprechpartner}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {customer.paket && <span style={{ ...styles.badge, background: '#E3F2FD', color: '#1565C0' }}>{customer.paket.toUpperCase()}</span>}
                    <span style={{ ...styles.badge, ...styles.badgeActive }}>{customer.status}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Edit Customer Modal */}
        {editingCustomer && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Kunde bearbeiten</h3>
                <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setEditingCustomer(null)}>✕</button>
              </div>
              <input style={styles.input} placeholder="Firma" value={editingCustomer.firma} onChange={e => setEditingCustomer({...editingCustomer, firma: e.target.value})} />
              <input style={styles.input} placeholder="Ansprechpartner" value={editingCustomer.ansprechpartner} onChange={e => setEditingCustomer({...editingCustomer, ansprechpartner: e.target.value})} />
              <input style={styles.input} placeholder="E-Mail" value={editingCustomer.email || ''} onChange={e => setEditingCustomer({...editingCustomer, email: e.target.value})} />
              <input style={styles.input} placeholder="Telefon" value={editingCustomer.telefon || ''} onChange={e => setEditingCustomer({...editingCustomer, telefon: e.target.value})} />
              <select style={styles.select} value={editingCustomer.paket || ''} onChange={e => setEditingCustomer({...editingCustomer, paket: e.target.value})}>
                <option value="">Kein Paket</option>
                <option value="visibility">VISIBILITY</option>
                <option value="growth">GROWTH</option>
                <option value="scale">SCALE</option>
              </select>
              <select style={styles.select} value={editingCustomer.status} onChange={e => setEditingCustomer({...editingCustomer, status: e.target.value})}>
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
                <option value="abgeschlossen">Abgeschlossen</option>
              </select>
              <textarea style={styles.textarea} placeholder="Notizen" value={editingCustomer.notizen || ''} onChange={e => setEditingCustomer({...editingCustomer, notizen: e.target.value})} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={styles.primaryBtnFilled} onClick={async () => {
                  await updateCustomer(editingCustomer.id, editingCustomer);
                  setEditingCustomer(null);
                }}>Speichern</button>
                <button style={styles.primaryBtn} onClick={() => setEditingCustomer(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {showDeleteConfirm && (
          <div style={styles.modal}>
            <div style={{ ...styles.modalContent, maxWidth: '400px', textAlign: 'center' }}>
              <h3 style={{ marginBottom: '15px' }}>Kunde löschen?</h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                "{showDeleteConfirm.firma}" wirklich löschen? Das Projekt wird ebenfalls gelöscht.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button style={styles.dangerBtn} onClick={() => deleteCustomer(showDeleteConfirm.id)}>Ja, löschen</button>
                <button style={styles.primaryBtn} onClick={() => setShowDeleteConfirm(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // STONEART VIEW (Idea Hub)
  // ═══════════════════════════════════════════════════════════════════════════════
  const StoneartView = () => (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px' }}>🎨 Stoneart</h2>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>💡 Idea Hub</h3>
        </div>
        
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <input 
            style={{ ...styles.input, marginBottom: 0, flex: 1 }} 
            placeholder="Neue Idee eingeben..."
            value={newIdeaText}
            onChange={e => setNewIdeaText(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter' && newIdeaText.trim()) {
                createIdea(newIdeaText);
                setNewIdeaText('');
              }
            }}
          />
          <button style={styles.primaryBtnFilled} onClick={() => {
            if (newIdeaText.trim()) {
              createIdea(newIdeaText);
              setNewIdeaText('');
            }
          }}>+ Hinzufügen</button>
        </div>

        {brandIdeas.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>Noch keine Ideen gespeichert</p>
        ) : (
          brandIdeas.map(idea => (
            <div 
              key={idea.id} 
              style={{ 
                padding: '15px', 
                borderBottom: '1px solid #E5E5E5', 
                cursor: 'pointer',
                borderLeft: `3px solid ${getTeamMemberColor(idea.created_by)}`
              }}
              onClick={() => { setEditingIdea(idea); setIdeaNotes(idea.notes || ''); }}
            >
              <div style={{ fontSize: '0.95rem' }}>{idea.text}</div>
              {idea.notes && (
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                  📝 {idea.notes.substring(0, 50)}{idea.notes.length > 50 ? '...' : ''}
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '5px' }}>
                von {getTeamMemberName(idea.created_by)} • {new Date(idea.created_at).toLocaleDateString('de-DE')}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROJEKTE VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  const ProjekteView = () => (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px' }}>Projekte</h2>

      {selectedProject ? (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h3 style={styles.cardTitle}>{selectedProject.customer_name}</h3>
              {selectedProject.paket && (
                <span style={{ ...styles.badge, background: '#E3F2FD', color: '#1565C0', marginTop: '5px', display: 'inline-block' }}>
                  {selectedProject.paket.toUpperCase()}
                </span>
              )}
            </div>
            <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setSelectedProject(null)}>← Zurück</button>
          </div>

          <h4 style={{ marginTop: '20px', marginBottom: '15px' }}>Projekt-Steps</h4>
          {selectedProject.steps?.map(step => {
            const completedByColor = step.completed_by ? getTeamMemberColor(step.completed_by) : null;
            const completedByName = step.completed_by ? getTeamMemberName(step.completed_by) : null;
            
            return (
              <div key={step.id} style={{
                ...styles.stepItem,
                background: step.status === 'completed' ? `${completedByColor}15` : 'transparent',
                borderLeft: step.status === 'completed' ? `3px solid ${completedByColor}` : '3px solid transparent',
                cursor: 'pointer'
              }}>
                <div 
                  style={{
                    ...styles.checkbox,
                    ...(step.status === 'completed' ? { ...styles.checkboxChecked, background: completedByColor, borderColor: completedByColor } : {})
                  }} 
                  onClick={(e) => { e.stopPropagation(); toggleStepStatus(selectedProject.id, step.id); }}
                >
                  {step.status === 'completed' && '✓'}
                </div>
                <div 
                  style={{ flex: 1 }}
                  onClick={() => { setEditingStep(step); setStepNotes(step.notes || ''); }}
                >
                  <div style={{
                    fontWeight: '600',
                    textDecoration: step.status === 'completed' ? 'line-through' : 'none',
                    color: step.status === 'completed' ? completedByColor : '#1A1A1A'
                  }}>
                    {step.name}
                    {step.notes && <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#999' }}>📝</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '3px' }}>
                    Deadline: {step.deadline}
                    {step.status === 'completed' && completedByName && (
                      <span style={{ marginLeft: '10px', color: completedByColor }}>
                        ✓ von {completedByName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.card}>
          {projects.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Noch keine Projekte</p>
          ) : (
            projects.map(project => {
              const completedSteps = project.steps?.filter(s => s.status === 'completed').length || 0;
              const totalSteps = project.steps?.length || 0;
              
              return (
                <div 
                  key={project.id} 
                  style={{ padding: '15px', borderBottom: '1px solid #E5E5E5', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => setSelectedProject(project)}
                >
                  <div>
                    <strong>{project.customer_name}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      {completedSteps} / {totalSteps} Steps erledigt
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {project.paket && <span style={{ ...styles.badge, background: '#E3F2FD', color: '#1565C0' }}>{project.paket.toUpperCase()}</span>}
                    <div style={{ width: '100px', height: '8px', background: '#E5E5E5', borderRadius: '4px' }}>
                      <div style={{ width: `${totalSteps > 0 ? (completedSteps/totalSteps)*100 : 0}%`, height: '100%', background: '#E31E24', borderRadius: '4px' }} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // MAIN APP RENDER (authenticated)
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logoRed}>
          <span style={styles.logo}>Stone<span style={{ fontWeight: '300' }}>ART.</span></span>
          <span style={styles.versionBadge}>MISSION CONTROL {VERSION}</span>
        </div>
        
        <div className="user-menu-container" style={{ position: 'relative' }}>
          <div 
            style={{ 
              ...styles.userBadge, 
              cursor: 'pointer', 
              padding: '8px 12px', 
              borderRadius: '4px', 
              background: showUserMenu ? '#F5F5F5' : 'transparent',
              border: '1px solid #E5E5E5'
            }}
            onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
          >
            <div style={{ ...styles.userDot, background: currentUser?.color || '#666' }} />
            <span style={{ fontWeight: '600' }}>{currentUser?.name}</span>
            <span style={{ marginLeft: '5px', fontSize: '0.7rem' }}>▼</span>
          </div>
          
          {showUserMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '5px',
              background: '#FFF',
              border: '2px solid #E5E5E5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '200px',
              zIndex: 1000
            }}>
              <div style={{ padding: '12px 15px', borderBottom: '1px solid #E5E5E5', fontSize: '0.85rem', color: '#666' }}>
                {currentUser?.email}
              </div>
              <button 
                style={{ 
                  width: '100%', 
                  padding: '12px 15px', 
                  background: '#FFF', 
                  border: 'none', 
                  textAlign: 'left', 
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#D32F2F',
                  display: 'block'
                }}
                onClick={handleLogout}
              >
                🚪 Abmelden
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Navigation */}
      <nav style={styles.nav}>
        {[
          { id: 'dashboard', label: '📊 Dashboard' },
          { id: 'kunden', label: '👥 Kunden' },
          { id: 'projekte', label: '📁 Projekte' },
          { id: 'stoneart', label: '🎨 Stoneart' },
        ].map(item => (
          <button
            key={item.id}
            style={{ ...styles.navBtn, ...(activeTab === item.id ? styles.navBtnActive : {}) }}
            onClick={() => { setActiveTab(item.id); setSelectedCustomer(null); setSelectedProject(null); }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main style={styles.content}>
        {dataLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p style={{ color: '#666' }}>Daten werden geladen...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && <DashboardView />}
            {activeTab === 'kunden' && <KundenView />}
            {activeTab === 'projekte' && <ProjekteView />}
            {activeTab === 'stoneart' && <StoneartView />}
          </>
        )}
      </main>

      {/* Step Notes Modal */}
      {editingStep && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>{editingStep.name}</h3>
              <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setEditingStep(null)}>✕</button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
              Deadline: {editingStep.deadline}
              {editingStep.status === 'completed' && editingStep.completed_by && (
                <span style={{ marginLeft: '10px', color: getTeamMemberColor(editingStep.completed_by) }}>
                  ✓ Erledigt von {getTeamMemberName(editingStep.completed_by)}
                </span>
              )}
            </p>
            <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Notizen</label>
            <textarea 
              style={styles.textarea}
              placeholder="Notizen zu diesem Step..."
              value={stepNotes}
              onChange={e => setStepNotes(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={styles.primaryBtnFilled} onClick={() => {
                updateStepNotes(selectedProject.id, editingStep.id, stepNotes);
                setEditingStep(null);
              }}>Speichern</button>
              <button style={styles.primaryBtn} onClick={() => setEditingStep(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Idea Notes Modal */}
      {editingIdea && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>💡 Idee</h3>
              <button style={{ ...styles.primaryBtn, padding: '8px 16px' }} onClick={() => setEditingIdea(null)}>✕</button>
            </div>
            <div style={{ padding: '15px', background: '#FFF9E6', marginBottom: '15px', fontSize: '0.95rem' }}>
              {editingIdea.text}
            </div>
            <label style={{ fontWeight: '600', marginBottom: '5px', display: 'block' }}>Notizen</label>
            <textarea 
              style={styles.textarea}
              placeholder="Weitere Notizen zu dieser Idee..."
              value={ideaNotes}
              onChange={e => setIdeaNotes(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
              <button style={styles.dangerBtn} onClick={() => {
                if (window.confirm('Idee wirklich löschen?')) {
                  deleteIdea(editingIdea.id);
                }
              }}>🗑️ Löschen</button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={styles.primaryBtn} onClick={() => setEditingIdea(null)}>Abbrechen</button>
                <button style={styles.primaryBtnFilled} onClick={() => {
                  updateIdeaNotes(editingIdea.id, ideaNotes);
                  setEditingIdea(null);
                }}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
